import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_WEBHOOK_PATH } from '../../constants.ts';
import { GET_WEBHOOKS, UPDATE_WEBHOOK } from '../../queries.ts';

const EVENT_ACTIONS = [
    'assistance_needed',
    'assisted',
    'before_generate_output',
    'output_generated',
    'before_send_output',
    'output_sent',
    'finished',
] as const;

const FORM_DEFAULT_VALUES = {
    name: '',
    enabled: true,
    url: '',
    authType: '',
    authCredentials: '',
    authHeader: '',
    username: '',
    password: '',
    events: [] as string[],
    showUnfinishedData: false,
    orderChannelIds: [] as string[],
    orderConfirmationChannelIds: [] as string[],
    rfqChannelIds: [] as string[],
    invoiceChannelIds: [] as string[],
    deliveryNoteChannelIds: [] as string[],
};

const EventsField = ({ control, formErrors }: { control: any; formErrors: any }) => {
    const { t } = useTranslation('integrations');

    return (
        <Controller
            name="events"
            control={control}
            rules={{ validate: (value) => value.length > 0 }}
            render={({ field }) => {
                const toggleAction = (action: (typeof EVENT_ACTIONS)[number]) => {
                    const current = field.value as string[];
                    const next = current.includes(action) ? current.filter((a) => a !== action) : [...current, action];
                    field.onChange(next);
                };

                return (
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-content-strong">
                            {t('webhook.form.eventActions')}
                        </span>
                        <div className="flex flex-col gap-1">
                            {EVENT_ACTIONS.map((action) => (
                                <label
                                    key={action}
                                    className="flex gap-2 items-center cursor-pointer text-sm text-content-medium"
                                >
                                    <Checkbox
                                        checked={(field.value as string[]).includes(action)}
                                        onChange={() => toggleAction(action)}
                                    />
                                    {t(`webhook.form.eventActionLabels.${action}`)}
                                </label>
                            ))}
                        </div>
                    </div>
                );
            }}
        />
    );
};

const WebhookEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const backTooltipText = `${t('backTooltipText')} ${t('webhook.title')}`;
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const { data, refetch } = useQuery(GET_WEBHOOKS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.webhooks?.find((w) => w.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];
    const invoiceChannels = channelData?.invoiceProcessingChannels || [];
    const deliveryNoteChannels = channelData?.deliveryNoteProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                enabled: integration.enabled !== undefined ? integration.enabled : true,
                url: integration.url || '',
                authType: integration.authType || '',
                authCredentials: '',
                authHeader: integration.authHeader || '',
                username: integration.username || '',
                password: '',
                events: [
                    ...new Set(
                        (JSON.parse(integration.events || '[]') as string[]).map((e: string) => e.split('.').pop())
                    ),
                ],
                showUnfinishedData: integration.showUnfinishedData || false,
                orderChannelIds: integration.orderChannels?.map((c) => c.id) || [],
                orderConfirmationChannelIds: integration.orderConfirmationChannels?.map((c) => c.id) || [],
                rfqChannelIds: integration.rfqChannels?.map((c) => c.id) || [],
                invoiceChannelIds: integration.invoiceChannels?.map((c) => c.id) || [],
                deliveryNoteChannelIds: integration.deliveryNoteChannels?.map((c) => c.id) || [],
            });
        }
    }, [integration]);

    const [updateWebhook, { loading: isSubmitting }] = useMutation(UPDATE_WEBHOOK);

    const handleSubmit = async (formData) => {
        try {
            await updateWebhook({
                variables: {
                    webhookId: integration.id,
                    name: formData.name,
                    url: formData.url,
                    enabled: formData.enabled,
                    events: formData.events,
                    authType: formData.authType || null,
                    authCredentials: formData.authCredentials || null,
                    authHeader: formData.authHeader || null,
                    username: formData.username || null,
                    password: formData.password || null,
                    showUnfinishedData: formData.showUnfinishedData,
                    orderChannels: formData.orderChannelIds,
                    orderConfirmationChannels: formData.orderConfirmationChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                    invoiceChannels: formData.invoiceChannelIds,
                    deliveryNoteChannels: formData.deliveryNoteChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('webhook.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_WEBHOOK_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('webhook.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('webhook.title'), to: url(CUSTOMER_INTEGRATION_WEBHOOK_PATH) },
                            { label: integration?.name || t('webhook.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard onSubmit={form.handleSubmit(handleSubmit)} title={t('webhook.form.updateTitle')}>
                        <Controller
                            name="name"
                            control={form.control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="enabled"
                            control={form.control}
                            render={({ field }) => (
                                <label className="flex gap-3 items-start cursor-pointer">
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                    <div className="flex flex-col gap-1">
                                        <span className="text-content-strong text-sm">{t('webhook.form.enabled')}</span>
                                        <span className="text-content-medium text-xs">
                                            {t('webhook.form.enabledDescription')}
                                        </span>
                                    </div>
                                </label>
                            )}
                        />

                        <Controller
                            name="url"
                            control={form.control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.url')} error={formErrors.url}>
                                    <StringField {...field} placeholder="https://api.example.com/webhook" />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="authType"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.authType')}>
                                    <StringField {...field} placeholder="e.g. Bearer, JWT" />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="authCredentials"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('webhook.form.authCredentials')}
                                    helpText={integration?.authCredentials ? t('secretUpdateOnly') : undefined}
                                >
                                    <SecretField
                                        {...field}
                                        secretName="authCredentials"
                                        secretId={integration?.id}
                                        defaultIsVisible={false}
                                        placeholder={integration?.authCredentials ? '••••••••' : undefined}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="authHeader"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.authHeader')}>
                                    <StringField {...field} placeholder="Authorization" />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="username"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.username')}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="password"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('webhook.form.password')}
                                    helpText={integration?.password ? t('secretUpdateOnly') : undefined}
                                >
                                    <SecretField
                                        {...field}
                                        secretName="password"
                                        secretId={integration?.id}
                                        defaultIsVisible={false}
                                        placeholder={integration?.password ? '••••••••' : undefined}
                                    />
                                </FormField>
                            )}
                        />

                        <div className="flex flex-col gap-3">
                            <span className="text-sm font-medium text-content-strong">{t('webhook.form.events')}</span>
                            {formErrors.events && (
                                <span className="text-xs text-content-danger-strong">
                                    {t('webhook.form.eventsRequired')}
                                </span>
                            )}
                            <EventsField control={form.control} formErrors={formErrors} />
                        </div>

                        <Controller
                            name="showUnfinishedData"
                            control={form.control}
                            render={({ field }) => (
                                <label className="flex gap-3 items-start cursor-pointer">
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                    <div className="flex flex-col gap-1">
                                        <span className="text-content-strong text-sm">
                                            {t('webhook.form.showUnfinishedData')}
                                        </span>
                                        <span className="text-content-medium text-xs">
                                            {t('webhook.form.showUnfinishedDataDescription')}
                                        </span>
                                    </div>
                                </label>
                            )}
                        />

                        <div className="flex flex-col gap-3">
                            <span className="text-sm font-medium text-content-strong">
                                {t('webhook.form.channels')}
                            </span>
                            {form.watch('orderChannelIds').length === 0 &&
                                form.watch('orderConfirmationChannelIds').length === 0 &&
                                form.watch('rfqChannelIds').length === 0 &&
                                form.watch('invoiceChannelIds').length === 0 &&
                                form.watch('deliveryNoteChannelIds').length === 0 && (
                                    <span className="text-xs text-content-medium">
                                        {t('webhook.form.channelsAllInfo')}
                                    </span>
                                )}

                            {orderChannels.length > 0 && (
                                <Controller
                                    name="orderChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField label={t('webhook.form.orderChannels')}>
                                            <CheckboxGroup
                                                {...field}
                                                options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />
                            )}

                            {orderConfirmationChannels.length > 0 && (
                                <Controller
                                    name="orderConfirmationChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField label={t('webhook.form.orderConfirmationChannels')}>
                                            <CheckboxGroup
                                                {...field}
                                                options={orderConfirmationChannels.map((c) => ({
                                                    value: c.id,
                                                    label: c.name,
                                                }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />
                            )}

                            {rfqChannels.length > 0 && (
                                <Controller
                                    name="rfqChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField label={t('webhook.form.rfqChannels')}>
                                            <CheckboxGroup
                                                {...field}
                                                options={rfqChannels.map((c) => ({ value: c.id, label: c.name }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />
                            )}

                            {invoiceChannels.length > 0 && (
                                <Controller
                                    name="invoiceChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField label={t('webhook.form.invoiceChannels')}>
                                            <CheckboxGroup
                                                {...field}
                                                options={invoiceChannels.map((c) => ({ value: c.id, label: c.name }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />
                            )}

                            {deliveryNoteChannels.length > 0 && (
                                <Controller
                                    name="deliveryNoteChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField label={t('webhook.form.deliveryNoteChannels')}>
                                            <CheckboxGroup
                                                {...field}
                                                options={deliveryNoteChannels.map((c) => ({
                                                    value: c.id,
                                                    label: c.name,
                                                }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />
                            )}
                        </div>

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_WEBHOOK_PATH)}>
                                    {t('webhook.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default WebhookEditPage;
